import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { WORKBENCH_HANDLED_ALERTS, WORKBENCH_PAGE_SIZE } from '../graphql/queries/workbenchBoard';
import { WorkbenchAlert } from '../types';

/**
 * 告警栏「已处理」/ 复盘栏「已关闭」tab 的分页。
 *
 * 首屏 board 只带上游首段，翻页一律走 workbenchHandledAlerts；在首次拉到之前
 * 调用方仍用 board 的数据兜底，避免切 tab 瞬间空列表。
 */
const useHandledAlertPaging = (warehouseId: number | undefined, category: 'LIVE' | 'PERIOD') => {
    const [items, setItems] = useState<WorkbenchAlert[] | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [nextPage, setNextPage] = useState(1);
    const [fetchPage, { loading }] = useLazyQuery(WORKBENCH_HANDLED_ALERTS, {
        fetchPolicy: 'network-only',
    });

    const loadPage = useCallback(
        async (pageNum: number) => {
            if (!warehouseId) return;
            const res = await fetchPage({
                variables: { warehouseId, category, pageNum, pageSize: WORKBENCH_PAGE_SIZE },
            });
            const page = res.data?.workbenchHandledAlerts;
            if (!page) return;
            setItems((prev) => (pageNum === 1 ? page.items : [...(prev ?? []), ...page.items]));
            setHasMore(page.hasMore);
            setNextPage(pageNum + 1);
        },
        [category, fetchPage, warehouseId]
    );

    const loadFirstPageOnce = useCallback(() => {
        if (items === null) loadPage(1);
    }, [items, loadPage]);

    const loadNextPage = useCallback(() => loadPage(nextPage), [loadPage, nextPage]);

    // 刚处理完一条告警时调用：清空已拉到的页，让下次切到该 tab 重新从第一页拉。
    // 不在此处直接重拉——处置动作只会发生在「实时」tab，此刻重拉的结果没人看
    const reset = useCallback(() => {
        setItems(null);
        setHasMore(false);
        setNextPage(1);
    }, []);

    return { items, hasMore, loading, loadFirstPageOnce, loadNextPage, reset };
};

export default useHandledAlertPaging;
